import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { employeesStore, generateEmployeeId } from './employeesStore';
import type { Employee, TeamId } from '../types';

export function useEmployees(): Employee[] {
  const cachedRef = useRef<Employee[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = employeesStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newEmployees = employeesStore.getSnapshot().context.employees;
    if (cachedRef.current.length === newEmployees.length &&
        cachedRef.current.every((e, i) => {
          const n = newEmployees[i];
          return n && e.id === n.id && e.team === n.team && e.name === n.name && e.color === n.color && e.skills === n.skills;
        })) {
      return cachedRef.current;
    }
    cachedRef.current = newEmployees;
    return newEmployees;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useEmployeeById(employeeId: string): Employee | undefined {
  const employees = useEmployees();
  return useMemo(() => employees.find(e => e.id === employeeId), [employees, employeeId]);
}

export function useEmployeesActions() {
  return useMemo(() => ({
    addEmployee: (employee: Employee) =>
      employeesStore.send({ type: 'employee.add', employee }),
    addNewEmployee: (name: string, color: string, team: TeamId, skills: string) => {
      const id = generateEmployeeId(name);
      employeesStore.send({ type: 'employee.add', employee: { id, name, color, team, skills } });
      return id;
    },
    updateEmployee: (employeeId: string, updates: Partial<Omit<Employee, 'id'>>) =>
      employeesStore.send({ type: 'employee.update', employeeId, updates }),
    deleteEmployee: (employeeId: string) =>
      employeesStore.send({ type: 'employee.delete', employeeId }),
    resetEmployees: () =>
      employeesStore.send({ type: 'employees.reset' }),
    importEmployees: (employees: Employee[]) =>
      employeesStore.send({ type: 'employees.import', employees }),
  }), []);
}
